import { format, isValid } from 'date-fns'
import * as Icon from 'lucide-react'
import React, { useRef, useState, HTMLAttributes, useEffect } from 'react'
import {Button, Calendar, CalendarCell, CalendarGrid, CalendarGridBody, CalendarGridHeader, CalendarHeaderCell, DateInput as DateInputter, DatePicker, DateSegment, DateValue, Dialog, Group, Heading, Label, Popover} from 'react-aria-components';
import { CalendarDate, CalendarDateTime, parseDate, ZonedDateTime } from '@internationalized/date';
import { cn } from '../../components/lib/utils'
import { cva, VariantProps } from 'class-variance-authority';


const dateGroupVariants = cva(
    "flex items-center justify-between w-full bg-white rounded-md ",
    {
        variants: {
            variant: {
                default: "border shadow-sm",
                inline: ""
            },
            size: {
                default: "",
                sm: ""
            }
        },
        defaultVariants: {
            variant: "default",
            size: "default"
        }
    }
)

const dateInputVariants = cva(
    "flex items-center py-1 text-sm w-full", 
    {
        variants: {
            variant: {
                default: "px-2",
                inline: ""
            },
            size: {
                default: "h-9",
                sm: "h-8"
            }
        },
        defaultVariants: {
            variant: "default",
            size: "default"
        }
    }
)

interface Props extends VariantProps<typeof dateGroupVariants> {
    value?: Date
    onValueChange?: (v:Date | null | undefined) => void
    disabled?: boolean
    hideIcon?: boolean
}


const NewDateInput = ({value:default_date, onValueChange, disabled, size, variant, hideIcon=false, ...props}:Props & HTMLAttributes<HTMLInputElement>) => {
    const [date, setdate] = useState<DateValue | undefined | null>(null);

    React.useEffect(() => {
        if(default_date) {
            if(typeof default_date === 'object'){
                setdate(parseDate(format(default_date, "yyyy-MM-dd")))
            }
        }
    }, [default_date])

    const handleChange = (date:CalendarDate | CalendarDateTime | ZonedDateTime | null) => {
        setdate(date);
        onValueChange && onValueChange(date?.toDate('CET'));
    }

  return (
    <DatePicker
        value={date}
        onChange={handleChange}
        isDisabled={disabled}
    >
        <Label className='sr-only'>Date</Label>
        <Group className={cn(dateGroupVariants({ variant, size }))}>
            <DateInputter className={cn(dateInputVariants({ variant, size }))}>
            {(segment) => {
                return (
                <DateSegment 
                    segment={{...segment,
                        text: segment.type === "literal" ? segment.text.replace(/-/g, "/") : segment.text,
                    }} 
                    className={cn("focus:outline-none focus:bg-primary focus:text-primary-foreground px-0.5 rounded-sm caret-transparent",
                        segment.isPlaceholder && "text-muted-foreground",
                        (segment.type === "literal" && !isValid(date?.toDate("CET"))) && "text-muted-foreground"
                    )}
                />
                )
            }}
            </DateInputter>
            {!hideIcon && <Button className="w-6 h-6 -ml-1 duration-300 text-neutral-400 hover:text-neutral-600"><Icon.CalendarIcon className='w-4 h-4'/></Button>}
        </Group>
        <Popover className="z-50 p-4 bg-white border shadow-md outline-none rounded-xl text-popover-foreground data-[entering]:animate-in data-[entering]:fade-in data-[exiting]:animate-out data-[exiting]:fade-out">
            <Dialog>
                <Calendar className="text-sm">
                    <header className='flex items-center justify-between gap-4 mb-3'>
                        <Button slot="previous" className="flex items-center justify-center w-6 h-6 border rounded-md"><Icon.ChevronLeftIcon className='w-4 h-4'/></Button>
                        <Heading className='text-sm font-medium capitalize'/>
                        <Button slot="next" className="flex items-center justify-center w-6 h-6 border rounded-md"><Icon.ChevronRightIcon className='w-4 h-4'/></Button>
                    </header>

                    <CalendarGrid>
                        <CalendarGridHeader>
                            {(day) => <CalendarHeaderCell className="text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]">{day}</CalendarHeaderCell>}
                        </CalendarGridHeader>
                        <CalendarGridBody>
                            {(date) => <CalendarCell date={date} className={cn(
                                "inline-flex items-center justify-center gap-2 p-0 text-center rounded-md w-9 h-9 hover:bg-accent whitespace-nowrap"
                            )}/>}
                        </CalendarGridBody>
                    </CalendarGrid>
                </Calendar>
            </Dialog>
        </Popover>
    </DatePicker>
    );
}

const DateInput = ({value, onValueChange, disabled, size, ...props}:Props & HTMLAttributes<HTMLInputElement>) => {

    const inputRef = useRef<HTMLInputElement>(null);
    const [dateString, setDateString] = useState<string>('DD/MM/JJJJ');

    useEffect(()=>{
        onValueChange && onValueChange(new Date(parseInt(dateString.split('/')[2]), (parseInt(dateString.split('/')[1])-1), parseInt(dateString.split('/')[0])))
    }, [dateString])

    useEffect(()=>{
        value && isValid(value) && setDateString(format(value, "dd/MM/yyyy"))
    }, [value])

    const handleClick = (start: number, end: number) => {
        window.setTimeout(()=>{
        inputRef.current && inputRef.current.setSelectionRange(start, end); 
        }, 0) 
    };

    const handleChange = (newValue: string, cursorPos: number) => {
        while (newValue.split('/').length < 3) {
            newValue += '/'; // Append slash to ensure all parts are present
        }
        while (newValue.split('/').length > 3) {
            newValue = newValue.substring(0, newValue.lastIndexOf('/'));
        }
        if (newValue === '') {
            setDateString('DD/MM/JJJJ');
            return;
        }
        
        const parts = newValue.split('/').map((part, index) => {
        let p = part
        
        if (index === 0) {
            part = part.replaceAll('J', 'D')
            part = part.replaceAll('M', 'D')
            // Day part
            if (part.length === 1 && parseFloat(part) > 3) {
                // If the value is single digit and greater than 3, prepend '0'
                p = `0${part}`;
            }
            if (part.length === 2 && parseFloat(part) > 31) {
                // If the value is two digits and greater than 31, truncate to '31'
                p = '31';
            }
            // If day part is removed, replace with 'DD'
            if (part.length === 0) {
                p = 'DD'
            }
            if(part.length > 2){ p = part.substring(0,2) }
            if (!/\d/.test(newValue)) {
                // If day part is completely removed (no digits), replace with 'JJJJ'
                return 'DD';
            }
        }
        else if (index === 1) {
            part = part.replaceAll('J', 'M')
            part = part.replaceAll('D', 'M')
            // Month part
            if (part.length === 1 && parseFloat(part) > 1) {
            // If the value is single digit and greater than 1, prepend '0'
            p = `0${part}`;
            }
            if (part.length === 2 && parseFloat(part) > 12) {
            // If the value is two digits and greater than 12, truncate to '12'
            p = '12';
            }
            // If month part is removed, replace with 'MM'
            if (part.length === 0) {
            p = 'MM';
            }
            if(part.length > 2){ p = part.substring(0,2) }
            if (!/\d/.test(newValue)) {
                // If month part is completely removed (no digits), replace with 'JJJJ'
                return 'MM';
            }
        }
        else if (index === 2) {
            part = part.replaceAll('M', 'J')
            part = part.replaceAll('D', 'J')
            // Year part
            if (part === '0' || part === '00' || part === '000' || part === '0000') {
                // If year part is '0', '00', '000', or '0000', replace with 'JJJJ'
                return 'JJJJ';
            }
            if (!/\d/.test(newValue)) {
                // If year part is completely removed (no digits), replace with 'JJJJ'
                return 'JJJJ';
            }
            if (part.length < 4) {
                // If year part is less than 4 characters, prepend '0's
                return '0'.repeat(4 - part.length) + part;
            }
            if (part.length > 4) {
                // If year part is more than 4 characters, truncate to 4 characters
                return part.slice(-4);
            }
        }
        return p;
        });

        let formattedValue = parts.join('/');

        setDateString(formattedValue);

        if (inputRef.current) {
            // Calculate the new cursor position
            let newPosition = cursorPos + (formattedValue.length - newValue.length);
            let endPosition = newPosition

            // Move cursor to the next segment if the cursor is at the end of a segment
            if (cursorPos === 2 || cursorPos === 5) {
                newPosition++;
                
                if(cursorPos === 2){ endPosition = endPosition+3 }
                if(cursorPos === 5){ endPosition = endPosition+5 }
            }
            
            if(newPosition === endPosition && newPosition === 2){ newPosition = 3; endPosition = 5 }
            else if(newPosition === endPosition && newPosition === 5){ newPosition = 6; endPosition = 10 }
            window.setTimeout(()=>{
                inputRef.current && inputRef.current.setSelectionRange(newPosition, endPosition); 
            }, 0) 
        }
    };

  return (
    <input 
        // size={size}
        disabled={disabled}
        ref={inputRef} 
        type="text" 
        value={dateString as string}
        onKeyDown={(e)=>{
            const direction = e.key == 'ArrowLeft' ? 'left' : (e.key === 'ArrowRight' ? 'right' : '')

            const cursorPos = e.currentTarget.selectionStart as number;
            if(cursorPos >= 0 && cursorPos <= 2 && direction === 'right'){ handleClick(3,5); return; }
            else if(cursorPos >= 0 && cursorPos <= 2 && direction === 'left'){ handleClick(0,2); return; }

            else if(cursorPos >= 3 && cursorPos <= 5 && direction === 'right'){ handleClick(6,10); return; }
            else if(cursorPos >= 3 && cursorPos <= 5 && direction === 'left'){ handleClick(0,2); return; }

            else if(cursorPos >= 6 && cursorPos <= 10 && direction === 'right'){ handleClick(6,10); return; }
            else if(cursorPos >= 6 && cursorPos <= 10 && direction === 'left'){ handleClick(3,5); return; }
        }}
        onClick={(e) => {
            const cursorPos = e.currentTarget.selectionStart as number;
            if (cursorPos >= 0 && cursorPos < 2) {
                handleClick(0, 2); // Select "DD"
            } else if (cursorPos >= 2 && cursorPos < 5) {
                handleClick(3, 5); // Select "MM"
            } else if (cursorPos >= 5) {
                handleClick(6, 10); // Select "JJJJ"
            }
        }}
        onChange={(e) => {
            const cursorPos = e.currentTarget.selectionStart as number;
            const newValue = e.target.value;
            handleChange(newValue, cursorPos);
        }}
        {...props}
    />
  )
}

export default NewDateInput